import { useCallback, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import useCalendar from '@/hooks/calendar/useCalendar';
import useSpecies from '@/hooks/species/useSpecies';
import { SpeciesAttributes, StrapiEntry } from '@/lib/apiTypes';
import { measure } from '@/components/Analytics/Analytics';
import useCards from '@/hooks/cards/useCards';
import InfoIcon from '@/components/Icon/InfoIcon';
import useHaptics from '@/hooks/useHaptics';
import CameraIcon from '@/components/Icon/CameraIcon';
import ArrowLeftIcon from '@/components/Icon/ArrowLeftIcon';

const SpeciesPicker = ({
	defaultSpeciesId,
	setCameraTargetSpeciesId,
	capture,
}: {
	defaultSpeciesId: number;
	setCameraTargetSpeciesId: (speciesId: number) => void;
	capture: (speciesId: number) => void;
}) => {
	const { withOnButtonPress } = useHaptics();

	const { findSpeciesById, getMainImageById } = useSpecies();
	const { getCardBySpeciesId } = useCards();
	const { calendar } = useCalendar();
	const { speciesOfTheWeekDate } = useCalendar();
	const [targetSpeciesId, setInternalTargetSpeciesId] = useState<
		number | null
	>(
		findSpeciesById(defaultSpeciesId)?.id ??
			calendar?.data[0].attributes.speciesEntry.data?.id ??
			0
	);
	const setTargetSpeciesId = useCallback(
		(speciesId: number) => {
			setInternalTargetSpeciesId(speciesId);
			setCameraTargetSpeciesId(speciesId);
		},
		[setCameraTargetSpeciesId]
	);
	const originalTargetSpeciesId = useRef(targetSpeciesId);

	// Check if targetSpeciesId is in calendar or not
	const isOriginalTargetSpeciesInCalendar = useMemo(() => {
		if (calendar) {
			return calendar.data.some(
				(entry) =>
					entry.attributes.speciesEntry.data?.id ===
					originalTargetSpeciesId.current
			);
		}
		return false;
	}, [calendar, originalTargetSpeciesId]);

	// List all species sorted by calendar order, insert original target species if not in calendar
	const speciesSortedByCalendarOrder = useMemo(() => {
		if (calendar) {
			const calendarSpecies = calendar.data
				// Get full species info
				.map((entry) =>
					findSpeciesById(entry.attributes.speciesEntry.data?.id)
				)
				// Filter out entries that have already been collected and are not originalTargetSpeciesId
				.filter(
					(species) =>
						species &&
						(!getCardBySpeciesId(species.id) ||
							species.id === originalTargetSpeciesId.current)
				) as StrapiEntry<SpeciesAttributes>[];

			// If the original target species is not in the calendar, add it to the list right before the species of the week
			// Or if there is no species of the week, add it to the start of the list
			if (!isOriginalTargetSpeciesInCalendar) {
				// Find the species of the week and its index
				const speciesOfTheWeek = findSpeciesById(
					calendar.data.find(
						(entry) =>
							entry.attributes.date ===
							speciesOfTheWeekDate?.toISOString()
					)?.attributes.speciesEntry.data?.id
				);
				const speciesOfTheWeekIndex = calendarSpecies.findIndex(
					(species) => species?.id === speciesOfTheWeek?.id
				);

				// Add the target species to the list
				if (!originalTargetSpeciesId.current) return [];
				const targetSpecies = findSpeciesById(
					originalTargetSpeciesId.current
				);
				if (!targetSpecies) return [];

				// Insert the target species before the species of the week if it exists
				if (speciesOfTheWeek && speciesOfTheWeekIndex !== -1) {
					calendarSpecies.splice(
						speciesOfTheWeekIndex,
						0,
						targetSpecies
					);
				} else {
					// Otherwise, add it to the start of the list
					calendarSpecies.unshift(targetSpecies);
				}
			}

			return (
				calendarSpecies
					// Filter out double entries
					.filter(
						(species, index, array) =>
							array.findIndex(
								(entry) => entry?.id === species?.id
							) === index
					)
			);
		}
		return [];
	}, [
		calendar,
		isOriginalTargetSpeciesInCalendar,
		findSpeciesById,
		getCardBySpeciesId,
		speciesOfTheWeekDate,
	]);

	// Get the species before, the target and the next species (LOOP IF NEEDED)
	const speciesAroundTargetIds = useMemo(() => {
		if (speciesSortedByCalendarOrder) {
			const targetSpecies = speciesSortedByCalendarOrder.find(
				(entry) => entry?.id === targetSpeciesId
			);
			if (targetSpecies) {
				const targetIndex =
					speciesSortedByCalendarOrder.indexOf(targetSpecies);
				const getCloseBySpecies = (shiftBy: number) =>
					speciesSortedByCalendarOrder[
						(targetIndex +
							shiftBy +
							speciesSortedByCalendarOrder.length) %
							speciesSortedByCalendarOrder.length
					];
				const previousSpecies = getCloseBySpecies(-1);
				const nextSpecies = getCloseBySpecies(1);
				return [previousSpecies, targetSpecies, nextSpecies].map(
					(species) => findSpeciesById(species.id)
				) as StrapiEntry<SpeciesAttributes>[];
			}
		}
		return [];
	}, [speciesSortedByCalendarOrder, targetSpeciesId, findSpeciesById]);

	// Check if all species are unique
	// If so, we can show buttons to switch between them
	// If not, we only show one shutter button
	const shownSpeciesHaveNoDuplicates = useMemo(() => {
		return (
			new Set(speciesAroundTargetIds.map((species) => species?.id))
				.size === 3
		);
	}, [speciesAroundTargetIds]);

	if (speciesAroundTargetIds.length !== 3) return null;

	return (
		<motion.nav
			className="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-6 bg-gradient-to-b from-black/0 to-black/25 pb-2 xxs:pb-6"
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: 1,
				bottom: 0,
				transition: {
					duration: 1,
				},
			}}
		>
			<div className="flex items-center justify-center gap-4 xxs:gap-6">
				{(shownSpeciesHaveNoDuplicates
					? speciesAroundTargetIds
					: [speciesAroundTargetIds[1]]
				).map((singleSpecies, index) => (
					<div key={singleSpecies?.id}>
						{singleSpecies.id === targetSpeciesId && (
							<SpeciesInfoButton
								targetSpeciesId={targetSpeciesId}
								singleSpecies={singleSpecies}
							/>
						)}
						<motion.button
							layoutId={`CameraRoute__SpeciesPicker__Species-${singleSpecies.id}`}
							layoutDependency={singleSpecies.id}
							className={`relative rounded-full ${
								singleSpecies.id === targetSpeciesId
									? 'bg-accentOrCompatDarkest text-accentOrCompatLightest'
									: ''
							}`}
							aria-label={
								singleSpecies.id === targetSpeciesId
									? `Foto maken van ${singleSpecies.attributes.titleLong}`
									: `Wissel naar ${singleSpecies.attributes.titleLong}`
							}
							initial={{
								scale: 0,
							}}
							animate={{
								scale:
									singleSpecies.id === targetSpeciesId
										? 1
										: 0.85,
								y:
									singleSpecies.id === targetSpeciesId
										? 0
										: '0.8rem',
							}}
							whileHover={{
								scale:
									singleSpecies.id === targetSpeciesId
										? 1.175
										: 1,
							}}
							whileTap={{
								scale:
									singleSpecies.id === targetSpeciesId
										? 0.95
										: 0.75,
							}}
							onClick={() => {
								if (singleSpecies.id === targetSpeciesId) {
									capture(singleSpecies.id);
								} else {
									measure(
										`camera-species-${singleSpecies.id}-${singleSpecies.attributes.slug}`
									);
									setTargetSpeciesId(
										speciesAroundTargetIds[index]?.id ?? 0
									);
								}
							}}
							{...withOnButtonPress}
						>
							{singleSpecies.id === targetSpeciesId ? (
								// Ring and camera icon for shutter button
								<>
									<div className="absolute inset-0 size-full scale-125 rounded-full border-[0.25rem] border-white" />
									<CameraIcon className="absolute start-1/2 top-1/2 z-10 size-10 -translate-x-1/2 -translate-y-1/2 xxs:size-14 lg:size-14" />
								</>
							) : (
								// Arrow icon for left and right options
								<ArrowLeftIcon
									className={`absolute top-1/2 size-5 -translate-y-1/3 lg:size-5 ${
										index === 0
											? '-start-7'
											: '-end-7 -scale-x-100'
									}`}
								/>
							)}
							<motion.img
								layoutId={`CameraRoute__SpeciesPicker__Image-${singleSpecies.id}`}
								src={getMainImageById(singleSpecies.id)?.url}
								alt={getMainImageById(singleSpecies.id)?.alt}
								style={
									singleSpecies.id === targetSpeciesId
										? {}
										: {
												WebkitMaskImage: `url("/assets/masks/blob_square_${
													index + 2
												}.svg")`,
												maskImage: `url("/assets/masks/blob_square_${
													index + 2
												}.svg")`,
												maskSize: 'fill',
												maskRepeat: 'no-repeat',
												 
										  }
								}
								animate={{
									opacity:
										singleSpecies.id === targetSpeciesId
											? 0
											: 1,
								}}
								transition={{
									duration:
										singleSpecies.id === targetSpeciesId
											? 1
											: 0,
								}}
								className={`relative size-12 ${
									singleSpecies.id === targetSpeciesId
										? 'rounded-full object-cover xxs:size-20'
										: 'bg-white object-contain xxs:size-16'
								}`}
							/>
						</motion.button>
					</div>
				))}
			</div>
		</motion.nav>
	);
};

const SpeciesInfoButton = ({
	targetSpeciesId,
	singleSpecies,
}: {
	targetSpeciesId: number;
	singleSpecies: StrapiEntry<SpeciesAttributes>;
}) => {
	const navigate = useNavigate();
	const { withOnButtonPress } = useHaptics();
	const { getSpeciesRouteById, getMainImageById } = useSpecies();

	return (
		<motion.button
			aria-label={`${singleSpecies.attributes.titleLong} informatie`}
			className="absolute inset-x-0 -top-14 flex w-full items-center justify-center xxs:-top-16"
			initial={{
				y: '10%',
				opacity: 0,
				scale: 0.8,
			}}
			animate={{
				y: 0,
				opacity: 1,
				scale: 1,
			}}
			whileTap={{
				scale: 0.95,
			}}
			onClick={() => {
				measure(
					`camera-details-${singleSpecies.id}-${singleSpecies.attributes.slug}`
				);
				navigate(getSpeciesRouteById(targetSpeciesId));
			}}
			{...withOnButtonPress}
		>
			<h2 className="block whitespace-nowrap font-brand text-2xl">
				<div className="relative min-w-fit whitespace-nowrap pb-0.5 pe-3 pt-0 text-accentOrCompatLightest">
					<LabelBackground className="text-accentOrCompatDarkest" />
					<span className="relative whitespace-nowrap">
						<img
							src={getMainImageById(singleSpecies.id)?.url}
							alt={getMainImageById(singleSpecies.id)?.alt}
							className="me-2 inline-block size-10 shrink-0 scale-105 whitespace-nowrap bg-white object-contain"
							style={{
								WebkitMaskImage: `url("/assets/masks/blob_square_4.svg")`,
								maskImage: `url("/assets/masks/blob_square_4.svg")`,
								maskSize: 'fill',
								maskRepeat: 'no-repeat',
							}}
						/>
						{singleSpecies.attributes.titleShort}
						<InfoIcon className="ms-2 inline-block h-5 -translate-y-0.5 lg:h-5" />
					</span>
				</div>
			</h2>
		</motion.button>
	);
};

export default SpeciesPicker;
